import { z } from 'zod';
import { provenanceSourceSchema, defaultStatusSchema } from './agent.types.js';
import { dataTypeSchema } from './model.types.js';
// --- Shared: structured tool-error shape (agent-tool-reliability 2026-04) ---
/**
 * Structured tool-error entry for tools that expose recoverable failures to
 * the ReAct agent. Carried on the `errors[]` field of `SensitivityResult`,
 * `Sensitivity2DResult`, `GoalSeekResult`, `ExportSensitivityTabResult`,
 * `MarketDataOutput`, and `ApplyMarketDefaultsResult`. The shape is
 * intentionally rich so the LLM can self-correct on its next call without
 * having to guess: `UNKNOWN_INPUT` ships the `validInputs` menu,
 * `OUT_OF_RANGE` ships `acceptableRange`, `NO_CONVERGENCE` ships
 * `iterations` + `lastError`, `INVALID_ARGS` ships the Zod issue paths.
 *
 * The older string-array `errors` fields on `SetInputResult` and
 * `GetValuesResult` stay as-is — different tool contracts. This schema is
 * only for the 6 tools in the agent-tool-reliability scope.
 */
export const toolErrorSchema = z.object({
    code: z.string(),
    message: z.string(),
    inputName: z.string().optional(),
    validInputs: z.array(z.string()).optional(),
    acceptableRange: z.object({ min: z.number(), max: z.number() }).optional(),
    iterations: z.number().int().optional(),
    lastError: z.number().optional(),
    zodIssues: z
        .array(z.object({ path: z.string(), message: z.string() }))
        .optional(),
});
/**
 * Canonical tool-error codes (the 9 known values; `code: z.string()` stays
 * permissive so additions don't require a schema bump). The `COMPUTATION_TIMEOUT`
 * code (added by `operating-asset-e2e-validation` Phase 1 Session 3, 2026-04-29)
 * is the **recoverable** counterpart to the SSE-error `COMPUTATION_TIMEOUT`:
 * thrown post-hoc by the engine recompute wrapper when a single tool's compute
 * exceeded `ENGINE_RECOMPUTE_BUDGET_MS` (default 5s). The agent receives it via
 * the tool's `errors[]` and can retry with corrected inputs on the next turn.
 */
export const TOOL_ERROR_CODES = {
    UNKNOWN_INPUT: 'UNKNOWN_INPUT',
    UNKNOWN_OUTPUT: 'UNKNOWN_OUTPUT',
    OUT_OF_RANGE: 'OUT_OF_RANGE',
    NO_CONVERGENCE: 'NO_CONVERGENCE',
    COMPUTATION_ERROR: 'COMPUTATION_ERROR',
    INVALID_ARGS: 'INVALID_ARGS',
    TAB_WRITE_FAILED: 'TAB_WRITE_FAILED',
    NULL_CATEGORY_WARNING: 'NULL_CATEGORY_WARNING',
    COMPUTATION_TIMEOUT: 'COMPUTATION_TIMEOUT',
};
// --- next-variable-curation Phase 1 — collection-state SSOT contract ---
//
// Co-located here (ADR 0005) so `collectionStateSchema` is in lexical scope
// when `stateDeltaSchema` references it via `collectionState: collectionStateSchema.optional()`.
// No cross-module import edge; no import cycle risk.
/** next-variable-curation Phase 1 — a coherent group of related inputs the agent collects together. */
export const nextGroupSchema = z.object({
    /** Free-form group identity (sourced from inputDefinition `category` — z.string(), NOT an enum). */
    group: z.string(),
    /** Member input identities in this group, in collection order. */
    inputs: z.array(z.string()),
    /** Count of inputs in the group (== inputs.length; carried explicitly for the "+N more" banner render). */
    count: z.number(),
});
/** next-variable-curation Phase 1 — server-computed SSOT for the collection→analysis stage + next group. */
export const collectionStateSchema = z.object({
    /** Which stage the conversation is in: still collecting inputs, or all addressed → analysis. */
    stage: z.enum(['collection', 'analysis']),
    /** How many distinct inputs have been addressed (computed; Phase 2 populates). */
    addressedCount: z.number(),
    /** Total distinct collectable inputs (excludes single-valued-required + location inputs — Phase 3). */
    total: z.number(),
    /** The next group to collect; null once stage === 'analysis'. */
    nextGroup: nextGroupSchema.nullable(),
    /** Preview of the groups after `nextGroup`, in collection order (empty in analysis). */
    remainingGroups: z.array(nextGroupSchema),
});
// --- Shared: state-delta fragment for state-mutating tool results ---
//
// Mixed into 4 per-tool schemas via `.extend(stateDeltaSchema.shape)`:
// `setInputResultSchema`, `setTableInputsResultSchema`,
// `applyMarketDefaultsResultSchema`, `acceptDefaultsResultSchema`.
// (`generateExcelResultSchema` was the 5th consumer; tool removed 2026-05-20.)
// Existing per-tool fields (e.g. `set` for set_inputs, `applied` for
// apply_market_defaults) are PRESERVED — these four are ADDITIVE and have
// `.default(...)` so consumers that don't pre-populate them parse cleanly.
//
// The agent reads these to know:
//   `written`           — what fields this tool just set (canonical map view)
//   `recomputed`        — downstream output values after the engine recompute
//   `currentProvenance` — provenance flags AFTER the write (per-field)
//   `pendingDefaults`   — input field-names with `defaultStatus: null` and
//                         `source: 'default'` after this write (drives
//                         accept_defaults decisions)
//
// `goal_seek` is NOT in scope (snapshot/restore — not state-mutating per
// agents/CLAUDE.md tools table).
//
// Authored 2026-05-04 by `agents-context-injection-cost-optimization`
// Phase 2 Session 1 — see `phases/02-tool-result-enrichment.md`.
export const stateDeltaSchema = z.object({
    written: z.record(z.string(), z.unknown()).default({}),
    recomputed: z.record(z.string(), z.unknown()).default({}),
    currentProvenance: z
        .record(z.string(), z.object({
        source: provenanceSourceSchema,
        defaultStatus: defaultStatusSchema.optional(),
        confidence: z.number().min(0).max(1).optional(),
    }))
        .default({}),
    pendingDefaults: z.array(z.string()).default([]),
    // next-variable-curation Phase 1 — server-computed collection-state SSOT (Phase 2 populates; optional/additive).
    collectionState: collectionStateSchema.optional(),
});
// --- set_inputs tool ---
/**
 * Per-input next-suggestion surfaced by `set_inputs`. Field shape mirrors
 * the producer's `getNextDefaultInput()` return at
 * `packages/agents/src/lib/tools/tool-helpers.ts` so the web `<NextFieldGuide>`
 * toolbar pill (Phase 3 Session 4) can read it without a schema-vs-producer
 * field-name divergence. Required: `name` + `question` (the LLM-facing
 * prompt the agent uses to elicit the next value). Optional metadata
 * (`displayName`, `dataType`, `currentDefault`, `required`) tags along so
 * future consumers can render richer hints; today's consumer reads only
 * `name` + `question` for the pill copy.
 *
 * `.nullable().optional()` because the producer emits `null` (not
 * `undefined`) when there's no suggestion — both must parse cleanly.
 */
export const setInputNextSuggestionSchema = z.object({
    name: z.string(),
    question: z.string(),
    displayName: z.string().optional(),
    dataType: z.string().optional(),
    currentDefault: z.any().optional(),
    required: z.boolean().optional(),
});
export const setInputResultSchema = z
    .object({
    set: z.array(z.object({
        name: z.string(),
        value: z.any(),
        previous: z.any(),
    })),
    errors: z.array(z.string()),
    currentMetrics: z.record(z.string(), z.any()),
    warnings: z.array(z.string()),
    nextSuggestion: setInputNextSuggestionSchema.nullable().optional(),
})
    .extend(stateDeltaSchema.shape);
// --- calculate tool ---
export const calculateResultSchema = z.object({
    summary: z.object({
        inputsSet: z.number(),
        inputsTotal: z.number(),
        inputsRemaining: z.number(),
        requiredMissing: z.array(z.string()),
    }),
    inputs: z.array(z.object({
        name: z.string(),
        displayName: z.string(),
        value: z.any(),
        isDefault: z.boolean(),
        displayFormat: z.string(),
    })),
    outputs: z.array(z.object({
        name: z.string(),
        displayName: z.string(),
        value: z.any(),
        displayFormat: z.string(),
    })),
    validationIssues: z.array(z.object({
        field: z.string(),
        severity: z.string(),
        message: z.string(),
    })),
    readyForGeneration: z.boolean(),
});
// --- get_values tool ---
export const getValuesResultSchema = z.object({
    values: z.record(z.string(), z.object({
        value: z.any(),
        displayName: z.string(),
        formatted: z.string(),
    })),
    errors: z.array(z.string()),
});
// --- get_dependencies tool ---
export const getDependenciesResultSchema = z.object({
    name: z.string(),
    displayName: z.string().optional(),
    formula: z.string().optional(),
    currentValue: z.any(),
    upstream: z
        .array(z.object({
        name: z.string(),
        displayName: z.string(),
        value: z.any(),
        isInput: z.boolean(),
        depth: z.number(),
    }))
        .optional(),
    downstream: z
        .array(z.object({
        name: z.string(),
        displayName: z.string(),
        value: z.any(),
        isOutput: z.boolean(),
        depth: z.number(),
    }))
        .optional(),
});
// --- run_sensitivity tool ---
export const sensitivityRowSchema = z.object({
    inputValue: z.number(),
    inputFormatted: z.string(),
    outputs: z.record(z.string(), z.number()),
    isBase: z.boolean(),
});
export const sensitivityResultSchema = z.object({
    test: z.object({
        input: z.string(),
        inputDisplayName: z.string(),
        outputs: z.array(z.object({ name: z.string(), displayName: z.string() })),
        baseValue: z.number(),
        method: z.string(),
        stepSize: z.number(),
        steps: z.number(),
    }),
    results: z.array(sensitivityRowSchema),
    elasticity: z.record(z.string(), z.number()).optional(),
    /**
     * Structured recoverable failures surfaced to the ReAct agent. When
     * populated, `results` may be empty and `test` carries a best-effort
     * echo of the agent's request (synthetic defaults when input metadata
     * couldn't be resolved). See `toolErrorSchema` docs.
     */
    errors: z.array(toolErrorSchema).optional(),
});
// --- run_sensitivity_2d tool ---
export const sensitivity2DResultSchema = z.object({
    input1: z.object({
        name: z.string(),
        displayName: z.string(),
        values: z.array(z.number()),
    }),
    input2: z.object({
        name: z.string(),
        displayName: z.string(),
        values: z.array(z.number()),
    }),
    output: z.object({ name: z.string(), displayName: z.string() }),
    matrix: z.array(z.array(z.number())),
    /** Structured recoverable failures — see `toolErrorSchema`. */
    errors: z.array(toolErrorSchema).optional(),
});
// --- compare_scenarios tool ---
//
// Discriminated union on `kind` (Phase 3 Session 4, 2026-05-07). Two
// variants:
// - `'matrix'` — existing N-scenario side-by-side comparison. Producer at
//   `compare-scenarios.tool.ts` defaults `kind` arg to `'matrix'` for
//   back-compat; the return literal carries `kind: 'matrix'` literal so
//   the schema parses.
// - `'compact_3axis'` — fixed Downside / Base / Upside summary used by the
//   web `<ScenarioSummaryCard>` and the right-panel "Run Scenario Analysis"
//   action button. Each axis carries its own `changes` + `outputs`; the
//   `summary.signalByOutput` provides per-output narrative cues.
const compareScenarioEntrySchema = z.object({
    changes: z.record(z.string(), z.any()),
    outputs: z.record(z.string(), z.any()),
});
export const compareMatrixResultSchema = z.object({
    kind: z.literal('matrix'),
    scenarios: z.array(z.object({
        name: z.string(),
        changes: z.record(z.string(), z.any()),
        outputs: z.record(z.string(), z.any()),
    })),
    comparison: z.record(z.string(), z.any()).optional(),
});
export const compareCompact3AxisResultSchema = z.object({
    kind: z.literal('compact_3axis'),
    downside: compareScenarioEntrySchema,
    base: compareScenarioEntrySchema,
    upside: compareScenarioEntrySchema,
    summary: z
        .object({
        signalByOutput: z.record(z.string(), z.string()).optional(),
    })
        .optional(),
});
export const compareResultSchema = z.discriminatedUnion('kind', [
    compareMatrixResultSchema,
    compareCompact3AxisResultSchema,
]);
// --- goal_seek tool ---
export const goalSeekResultSchema = z.object({
    found: z.boolean(),
    inputValue: z.number().optional(),
    outputValue: z.number().optional(),
    iterations: z.number().optional(),
    message: z.string().optional(),
    /** Structured recoverable failures — see `toolErrorSchema`. */
    errors: z.array(toolErrorSchema).optional(),
});
// --- validate_input tool ---
export const validateInputResultSchema = z.object({
    valid: z.boolean(),
    normalizedValue: z.any().optional(),
    displayFormatted: z.string().optional(),
    issues: z.array(z.object({
        ruleId: z.string(),
        severity: z.string(),
        message: z.string(),
        suggestedFix: z.string().optional(),
    })),
    typicalRange: z.string().optional(),
    benchmarkSource: z.string().optional(),
});
// `generate_excel` tool REMOVED 2026-05-20 (Excel download deprecated; the
// in-platform Model tab is the canonical surface). The schema +
// `GenerateExcelResult` type were deleted; if a stored tool-result envelope
// from a pre-removal conversation still carries the legacy shape, the
// web `<ToolResultRenderer>` falls through its `default` case (renders
// nothing) — no parse crash.
// --- export_sensitivity_tab tool ---
export const exportSensitivityTabResultSchema = z.object({
    url: z.string(),
    tabName: z.string(),
    /** Structured recoverable failures — see `toolErrorSchema`. */
    errors: z.array(toolErrorSchema).optional(),
});
// --- get_model_info tool ---
export const modelInfoResultSchema = z.object({
    data: z.any(),
});
// --- accept_underwriting tool ---
export const acceptanceResultSchema = z.object({
    eventId: z.string(),
    versionLabel: z.string(),
    summary: z.string(),
});
// --- set_table_inputs tool ---
export const setTableInputsResultSchema = z
    .object({
    table: z.string(),
    mode: z.enum(['aggregate', 'per_row', 'mixed']),
    rowsSet: z.array(z.object({
        label: z.string(),
        values: z.record(z.string(), z.any()),
    })),
    aggregateApplied: z.record(z.string(), z.number()).optional(),
    currentMetrics: z.record(z.string(), z.number()),
    warnings: z.array(z.string()).default([]),
})
    .extend(stateDeltaSchema.shape);
// --- propose_table_fill tool (synthetic — agent-ux-optimization Phase 5) ---
/**
 * `propose_table_fill` is a read-only synthetic tool. The structured
 * proposal payload travels via the `tool_input_ready` SSE event's args,
 * NOT via this result. The input schema lives here (not just in the
 * agents package's tool file) because the web renderer (`<TableFillProposal>`)
 * needs the type — and `apps/web` does NOT import from `@arcanum-hq/agents`
 * by convention. The agents tool factory imports + re-uses
 * `proposeTableFillInputSchema` from this barrel.
 *
 * `dataTypeSchema` (re-imported here for use inside the columns array) is
 * the canonical 11-value enum from `model.types.ts`. Keeps the agent's
 * column dataType field aligned 1:1 with `TableInputDefinition.columns[]
 * .dataType` so the agent can copy column dataTypes verbatim from
 * `schema.tableInputs[]`.
 */
export const proposeTableFillInputSchema = z.object({
    table: z.string().min(1),
    rowLabels: z.array(z.string().min(1)).min(1),
    columns: z
        .array(z.object({
        key: z.string().min(1),
        label: z.string().min(1),
        dataType: dataTypeSchema,
    }))
        .min(1),
    // Cell values: number for numeric dataTypes; string for `string` /
    // `date` (date inputs return ISO 'YYYY-MM-DD' strings); boolean for
    // `boolean` dataType columns. The renderer's checkbox branch reads +
    // writes booleans directly.
    cells: z.record(z.string(), z.record(z.string(), z.union([z.number(), z.string(), z.boolean()]))),
    rationale: z.string().min(1),
    defaultsBasis: z.string().min(1),
});
/**
 * Result envelope is trivial `{ ok: true }` — the tool's value is in the
 * args (consumed by the chat surface via the `tool_input_ready` event).
 * Does NOT extend `stateDeltaSchema.shape` — read-only tool, no state delta.
 */
export const proposeTableFillResultSchema = z.object({
    ok: z.literal(true),
});
// --- market_data_enrichment tool ---
export { marketDataOutputSchema as marketDataEnrichmentResultSchema } from './market-data.types.js';
// --- apply_market_defaults tool ---
/**
 * 3-state alias match classification for apply_market_defaults partition.
 * (market-default-apply-ux Phase 1 Session 1.2)
 *
 * DISTINCT from the 4 existing source/provenance enums (wireSourceType,
 * wireDefaultStatus, modelEditRequest.source, ProvenanceSource) — this is
 * MATCH-state, not provenance. Do NOT conflate.
 *
 *   'applied'               — alias entry exists with a non-null target
 *   'intentionally_unmapped' — alias entry exists but target === null
 *   'genuinely_unmatched'   — no alias entry for this field at all
 *
 * Authoring precedent: wireSourceTypeSchema / wireDefaultStatusSchema in realtime.ts.
 */
export const aliasMatchStateSchema = z.enum([
    'applied',
    'intentionally_unmapped',
    'genuinely_unmatched',
]);
/**
 * Uniform item shape for the three alias-state partition arrays on
 * applyMarketDefaultsResultSchema. Internal — not barrel-exported unless
 * a Phase-2 consumer surfaces a standalone item-type need.
 * `unit` is optional — pv.unit can be undefined on the apply loop.
 */
const marketFieldValueUnitSchema = z.object({
    marketField: z.string(),
    value: z.number(),
    unit: z.string().optional(),
});
export const applyMarketDefaultsResultSchema = z
    .object({
    applied: z.array(z.object({
        inputName: z.string(),
        value: z.number(),
        marketField: z.string(),
        source: z.string(),
        confidence: z.enum(['high', 'medium', 'low']),
    })),
    skipped: z.array(z.object({
        marketField: z.string(),
        reason: z.string(),
    })),
    metro: z.string(),
    warnings: z.array(z.string()),
    /** Structured recoverable failures — see `toolErrorSchema`. */
    errors: z.array(toolErrorSchema).optional(),
    /**
     * Per-bedroom UnitMix rent writes (market-defaults-application Phase 2).
     * SCALAR `applied[]` cannot represent a {table,column} write; table cells
     * are accounted here + flattened into stateDelta.written under
     * `UnitMix.<RowLabel>.rent_month` composite keys. `.default([])` = additive.
     */
    appliedTableCells: z
        .array(z.object({
        table: z.string(),
        rowLabel: z.string(),
        column: z.string(),
        namedRange: z.string(),
        value: z.number(),
        marketField: z.string(),
        source: z.string(),
        confidence: z.enum(['high', 'medium', 'low']),
    }))
        .default([]),
    /**
     * Apply-result transparency accounting (market-defaults-application Phase 3,
     * spec §5). Derived agents-side from the write/skip arrays:
     *   fetched  = total market fields parsed this call (fields.length)
     *   applied  = applied.length + appliedTableCells.length (genuine writes)
     *   noTarget = skipped entries with reason 'No matching model input'
     * Additive .default(...) → back-compat for results recorded pre-Phase-3.
     */
    fetchedVsApplied: z
        .object({
        fetched: z.number().int().nonnegative(),
        applied: z.number().int().nonnegative(),
        noTarget: z.number().int().nonnegative(),
    })
        .default({ fetched: 0, applied: 0, noTarget: 0 }),
    /**
     * Alias-state partition arrays (market-default-apply-ux Phase 1 Session 1.2).
     * Three named sets of a UNIFORM {marketField, value, unit?} item — ADR 0005.
     *
     * appliedSet            — alias-state='applied' census (alias map intended a
     *                         write; actual write may still be skipped for a
     *                         non-alias reason). SUPERSET signal alongside applied[].
     * informationalSet      — alias-state='intentionally_unmapped' (target:null).
     *                         These are informational-only; never actionable.
     * genuinelyUnmatchedSet — alias-state='genuinely_unmatched' (no alias entry).
     *
     * Reconciliation invariant (research § 10 C-3):
     *   informationalSet.length + genuinelyUnmatchedSet.length === fetchedVsApplied.noTarget
     *
     * All three use .default([]) so:
     *   (a) the 2 error-exit early returns need not emit them, and
     *   (b) pre-feature tool_results parse back-compat.
     */
    appliedSet: z.array(marketFieldValueUnitSchema).default([]),
    informationalSet: z.array(marketFieldValueUnitSchema).default([]),
    genuinelyUnmatchedSet: z.array(marketFieldValueUnitSchema).default([]),
})
    .extend(stateDeltaSchema.shape);
// --- accept_defaults tool ---
export const acceptDefaultsResultSchema = z
    .object({
    accepted: z.array(z.string()),
    alreadySet: z.array(z.string()),
    count: z.number(),
})
    .extend(stateDeltaSchema.shape);
